import { useTranslation } from "react-i18next";
import { useAccounts } from "@/hooks/useAccounts";
import { useCategories } from "@/hooks/useCategories";
import { useAddTransaction, useTransactionTypes } from "@/hooks/useTransactions.ts";

/** Shape NewTransactionModal and QuickTransactionModal submit: names, not ids. */
export interface TransactionFormData {
  description?: string;
  name: string;
  amount: string;
  category: string;
  tag_id?: number | null;
  date: string;
  account: string;
  destination_account?: string;
  type: string;
  ticket_img?: string | null;
  saving_plan_id?: number | null;
  installment_id?: number | null;
}

const normalizeOptional = <T,>(value: T | null | undefined): T | undefined => value ?? undefined;

/**
 * Resolves the modal's account/category/type names to ids and POSTs the transaction.
 * Throws an Error with a user-facing message when the form references something unknown.
 */
export const useCreateTransactionFromForm = (options?: { enabled?: boolean }) => {
  const { t } = useTranslation("common");
  const { data: accounts = [] } = useAccounts(options);
  const { data: categories = [] } = useCategories(options);
  const { data: transactionTypes = [] } = useTransactionTypes(options);
  const addTransactionMutation = useAddTransaction();

  return async (transactionData: TransactionFormData) => {
    const account = accounts.find((item) => item.name === transactionData.account);
    if (!account) throw new Error(t("common.errors.selectedAccountNotFound"));

    const categoryId = transactionData.category
      ? categories.find((item) => item.name === transactionData.category)?.id
      : undefined;

    const typeLower = transactionData.type.toLowerCase();
    const matchedType = transactionTypes.find((item) => item.name.toLowerCase() === typeLower);
    let typeId = matchedType?.id ?? 2;
    if (!matchedType && typeLower === "deposit") typeId = 1;
    if (!matchedType && typeLower === "transfer") typeId = 3;

    let destinationAccountId: number | undefined;
    if (typeLower === "transfer") {
      if (!transactionData.destination_account) throw new Error(t("common.errors.transferDestinationRequired"));
      if (transactionData.destination_account === transactionData.account) {
        throw new Error(t("common.errors.destinationMustDiffer"));
      }
      const destinationAccount = accounts.find((item) => item.name === transactionData.destination_account);
      if (!destinationAccount) throw new Error(t("common.errors.selectedDestinationAccountNotFound"));
      destinationAccountId = destinationAccount.id;
    }

    await addTransactionMutation.mutateAsync({
      account_id: account.id,
      amount: parseFloat(transactionData.amount),
      category_id: categoryId,
      tag_id: normalizeOptional(transactionData.tag_id),
      type_id: typeId,
      transaction_date: transactionData.date,
      name: transactionData.name,
      ticket_img: transactionData.ticket_img,
      description: transactionData.description,
      saving_plan_id: normalizeOptional(transactionData.saving_plan_id),
      installment_id: normalizeOptional(transactionData.installment_id),
      destination_account_id: destinationAccountId,
    });
  };
};
